/**
 * Single entry point for pages: composes fileProcessor -> uploadQueue -> stateUploader.
 */

import * as fileProcessor from '@renderer/utils/pipeline/fileProcessor'
import { uploadFile } from '@renderer/utils/pipeline/uploadQueue'
import * as stateUploader from '@renderer/utils/pipeline/stateUploader'
import { summarizePhotoUrlsForUploadDebug } from '@renderer/utils/imageCacheUtils'
import type {
  ProcessingContext,
  UploadResult,
  WatermarkConfig,
  GalleryItemForProcessing,
  ProcessedFile,
  MediaType
} from '@renderer/types/outputPipeline.types'
import type { ProcessVideoTaskParams } from '@renderer/utils/pipeline/fileProcessor'

type SingleFileUploadType = 'strip' | 'gif' | 'image' | 'video'

function fileToProcessed(file: File, uploadType: SingleFileUploadType): ProcessedFile {
  const fileType: MediaType =
    uploadType === 'video' ? 'videos' : uploadType === 'gif' ? 'gifs' : 'photos'
  return {
    file,
    fileName: file.name,
    fileType,
    uploadType,
    metadata: { mimeType: file.type }
  }
}

export async function processAndUploadStrip(
  stripBlob: Blob,
  ctx: ProcessingContext
): Promise<UploadResult | null> {
  try {
    const processed = fileProcessor.processStrip(stripBlob, ctx.sessionId)
    const result = await uploadFile(processed, ctx)
    await stateUploader.logActivity(
      ctx.sessionId,
      `Strip upload completed: ${processed.fileName}`,
      'info',
      { fileName: processed.fileName },
      'outputOrchestrator'
    )
    return result
  } catch (error) {
    console.error('[outputOrchestrator] processAndUploadStrip failed:', error)
    await stateUploader.logActivity(
      ctx.sessionId,
      `Strip upload failed: ${error instanceof Error ? error.message : String(error)}`,
      'error',
      { error: String(error) },
      'outputOrchestrator'
    )
    return null
  }
}

/** Result of processAndUploadGIF: gifLocalUrl and optionally mp4LocalUrl (when 2 files). */
export interface ProcessAndUploadGIFResult {
  gifLocalUrl: string | null
  mp4LocalUrl?: string | null
}

export async function processAndUploadGIF(
  photoUrls: string[],
  gifSpeed: number,
  ctx: ProcessingContext,
  customGifFrame?: string,
  onSuccess?: (gifLocalUrl: string, mp4LocalUrl?: string | null) => void
): Promise<ProcessAndUploadGIFResult> {
  try {
    const processedList = await fileProcessor.processGIF(
      photoUrls,
      gifSpeed,
      ctx.sessionId,
      customGifFrame
    )
    const results = await Promise.all(processedList.map((p) => uploadFile(p, ctx)))
    const gifLocalUrl = results[0]?.localUrl ?? null
    const mp4LocalUrl = results[1]?.localUrl ?? null
    if (gifLocalUrl && onSuccess) onSuccess(gifLocalUrl, mp4LocalUrl)
    await stateUploader.logActivity(
      ctx.sessionId,
      `GIF/MP4 upload completed (${processedList.length} files)`,
      'info',
      {},
      'outputOrchestrator'
    )
    return { gifLocalUrl, mp4LocalUrl }
  } catch (error) {
    console.error('[outputOrchestrator] processAndUploadGIF failed:', error)
    await stateUploader.logActivity(
      ctx.sessionId,
      `GIF upload failed: ${error instanceof Error ? error.message : String(error)}`,
      'error',
      { error: String(error) },
      'outputOrchestrator'
    )
    return { gifLocalUrl: null, mp4LocalUrl: null }
  }
}

export async function processAndUploadPhotos(
  galleryItems: GalleryItemForProcessing[],
  ctx: ProcessingContext,
  watermarkConfig?: WatermarkConfig,
  onError?: (error: Error) => void
): Promise<void> {
  try {
    const processedList = await fileProcessor.processIndividualPhotos(
      galleryItems,
      ctx.sessionId,
      watermarkConfig
    )
    await Promise.all(processedList.map((p) => uploadFile(p, ctx)))
    await stateUploader.logActivity(
      ctx.sessionId,
      `Individual photos upload completed (${processedList.length} photos)`,
      'info',
      {},
      'outputOrchestrator'
    )
  } catch (error) {
    console.error('[outputOrchestrator] processAndUploadPhotos failed:', error)
    const err = error instanceof Error ? error : new Error(String(error))
    if (onError) onError(err)
    const debug = summarizePhotoUrlsForUploadDebug(galleryItems)
    console.warn('[outputOrchestrator] Photo upload failure context:', debug)
    await stateUploader.logActivity(
      ctx.sessionId,
      `Photos upload failed: ${err.message}`,
      'error',
      { error: err.message, ...debug },
      'outputOrchestrator'
    )
  }
}

export function processAndUploadVideo(
  params: ProcessVideoTaskParams
): string | null {
  try {
    return fileProcessor.processVideoTask(params)
  } catch (error) {
    console.error('[outputOrchestrator] processAndUploadVideo failed:', error)
    if (params.onError) {
      params.onError(error instanceof Error ? error : new Error(String(error)))
    }
    return null
  }
}

/**
 * Upload a single pre-built File (e.g. recording blob, strip from edit flow).
 * Use for Record.tsx, edit/Select, and Filter pages that upload one file at a time.
 */
export async function processAndUploadSingleFile(
  file: File,
  uploadType: SingleFileUploadType,
  ctx: ProcessingContext
): Promise<UploadResult> {
  try {
    const processed = fileToProcessed(file, uploadType)
    const result = await uploadFile(processed, ctx)
    await stateUploader.logActivity(
      ctx.sessionId,
      `${uploadType} upload completed: ${processed.fileName}`,
      'info',
      { fileName: processed.fileName },
      'outputOrchestrator'
    )
    return result
  } catch (error) {
    console.error('[outputOrchestrator] processAndUploadSingleFile failed:', error)
    await stateUploader.logActivity(
      ctx.sessionId,
      `${uploadType} upload failed: ${error instanceof Error ? error.message : String(error)}`,
      'error',
      { error: String(error) },
      'outputOrchestrator'
    )
    const localUrl = URL.createObjectURL(file)
    return { localUrl }
  }
}

export async function finalizeSession(sessionId: string): Promise<void> {
  await stateUploader.finalizeSession(sessionId)
}

export { stateUploader }
export type { ProcessVideoTaskParams } from '@renderer/utils/pipeline/fileProcessor'
